<template>
    <article class="mx-auto max-w-4xl text-left">
        <!-- Featured Image -->
        <div v-if="post.featuredImage" class="mb-8">
            <div class="rounded-lg overflow-hidden">
                <img
                    :src="blogImageSrc(post.featuredImage, 1200, 400)"
                    :srcset="blogImageSrcset(post.featuredImage, 1200, 400)"
                    :alt="post.featuredImageAlt || post.title"
                    width="1200"
                    height="400"
                    fetchpriority="high"
                    decoding="async"
                    class="w-full max-h-[200px] object-cover"
                />
            </div>
            <!-- Unsplash Attribution -->
            <div
                v-if="post.featuredImageAttribution?.source === 'unsplash'"
                class="mt-2 text-sm text-gray-500 dark:text-txt-body"
            >
                Foto od
                <a
                    :href="`https://unsplash.com/@${post.featuredImageAttribution.photographerUsername}?utm_source=growni&utm_medium=referral`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-gray-700 dark:text-txt-body hover:text-primary-600 hover:dark:text-primary-300 font-medium"
                >
                    {{ post.featuredImageAttribution.photographerName }}
                </a>
                na
                <a
                    href="https://unsplash.com?utm_source=growni&utm_medium=referral"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-gray-700 dark:text-txt-body hover:text-primary-600 hover:dark:text-primary-300 font-medium"
                >
                    Unsplash
                </a>
            </div>
        </div>

        <!-- Title -->
        <h1 class="blog-title mx-auto max-w-[27ch] text-gray-900 dark:text-txt-headings mb-6">
            {{ post.title }}
        </h1>

        <!-- Meta -->
        <div class="blog-reading-column mb-8 pb-8 border-b border-gray-200 dark:border-default">
            <BlogPostMeta
                link-author
                :slug="post.status === 'published' ? post.slug : undefined"
                :author="post.author"
                :published-at="post.publishedAt"
                :reading-time="post.readingTime"
            />
        </div>

        <!-- Shared article typography also used while writing. -->
        <div class="blog-prose">
            <template v-for="(segment, index) in contentSegments" :key="index">
                <BlogJobCard v-if="segment.jobSlug" :fallback-html="segment.html" :slug="segment.jobSlug" />
                <div v-else class="blog-content-segment" v-html="segment.html" />
            </template>
        </div>

        <!-- Tags -->
        <div class="mt-12 pt-8 border-t border-gray-200 dark:border-default">
            <BlogTagList :tags="post.tags" />
        </div>

        <!-- Author Bio (if available) -->
        <div v-if="post.author.bio" class="mt-12 p-6 bg-gray-50 dark:bg-subtle rounded-lg">
            <div class="flex items-start gap-4">
                <component
                    :is="post.author.id ? NuxtLink : 'div'"
                    v-if="post.author.profileImg"
                    :to="post.author.id ? `/u/${encodeURIComponent(post.author.id)}` : undefined"
                    :aria-label="post.author.id ? `Profil autora: ${post.author.name}` : undefined"
                    class="shrink-0 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                >
                    <img
                        :src="post.author.profileImg"
                        :alt="post.author.name"
                        class="size-16 rounded-full object-cover"
                    />
                </component>
                <div>
                    <p class="font-semibold text-gray-900 dark:text-txt-headings mb-1">
                        <NuxtLink
                            v-if="post.author.id"
                            :to="`/u/${encodeURIComponent(post.author.id)}`"
                            class="hover:underline"
                            >{{ post.author.name }}</NuxtLink
                        >
                        <template v-else>{{ post.author.name }}</template>
                    </p>
                    <div class="text-sm text-gray-600 dark:text-txt-body" v-html="sanitizedBio" />
                </div>
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { NuxtLink } from '#components'
import { sanitizeBlogHtml, sanitizeBlogBio } from '#shared/utils/blog-html'
import type { BlogPost } from '#shared/types/Blog'
import { blogImageSrc, blogImageSrcset } from '@/utils/blogImage'
import BlogJobCard from '@/components/blog/BlogJobCard.vue'

const props = defineProps<{
    post: BlogPost
}>()

// Ensure empty paragraphs render with proper height
function preserveEmptyParagraphs(html: string): string {
    // Replace empty <p></p> or <p> </p> with <p><br></p> to preserve spacing
    return html.replace(/<p><\/p>/g, '<p><br></p>').replace(/<p>\s*<\/p>/g, '<p><br></p>')
}

// Sanitize HTML content to prevent XSS
const sanitizedContent = computed(() => {
    if (!props.post.content) return '<p>Žiadny obsah</p>'
    const sanitized = sanitizeBlogHtml(props.post.content)
    return preserveEmptyParagraphs(sanitized)
})

/**
 * A top-level paragraph that holds nothing but a link to a GROWNi job (optionally after a short
 * prefix like an emoji) renders as a job card. Links in running text or list items stay links.
 */
const JOB_LINK_PARAGRAPH =
    /(?<!<li>)<p>\s*[^<]{0,4}<a\b[^>]*href="(?:https?:\/\/(?:www\.)?growni\.sk)?\/praca\/([\w-]+)[^"]*"[^>]*>[\s\S]*?<\/a>\s*<\/p>/g

// Editor content usually comes wrapped in <div class="wysiwyg-text">. Unwrap it so splitting into
// segments doesn't cut the wrapper in half. Blog prose supplies the shared typography.
const WYSIWYG_WRAPPER = /^\s*<div class="wysiwyg-text">([\s\S]*)<\/div>\s*$/

const contentSegments = computed(() => {
    const html = sanitizedContent.value.replace(WYSIWYG_WRAPPER, '$1')
    const segments: { html: string; jobSlug?: string }[] = []
    let lastIndex = 0
    for (const match of html.matchAll(JOB_LINK_PARAGRAPH)) {
        if (match.index > lastIndex) segments.push({ html: html.slice(lastIndex, match.index) })
        segments.push({ html: match[0], jobSlug: match[1] })
        lastIndex = match.index + match[0].length
    }
    if (lastIndex < html.length) segments.push({ html: html.slice(lastIndex) })
    return segments
})

// Sanitize author bio
const sanitizedBio = computed(() => {
    if (!props.post.author?.bio) return ''
    return sanitizeBlogBio(props.post.author.bio)
})
</script>
